import React, { useCallback, useState, useMemo } from "react";
import {
  Button,
  Flex,
  Heading,
  Input,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from "@chakra-ui/react";
import { ChevronDown } from "react-feather";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";

import HelpTooltip from "@/components/common/HelpTooltip";
import { useCloud } from "@/hooks/useCloud";
import { useDebounce } from "@/hooks/useDebounce";
import { CloudType, File, Tag, Folder } from "@/types/cloud";
import FileModal from "./FileModal";
import CloudFilters from "./Filters";
import FolderModal from "./FolderModal";
import CloudGrid from "./Grid";
import PreviewModal from "./PreviewModal";
import UploadFilesModal from "./UploadFilesModal";
import ChooseFolderToMoveModal from "./ChooseFolderToMoveModal";
import { useToastPromise } from "@/hooks/useToast";

type Props = {
  folderId: number;
};

const Cloud = ({ folderId }: Props): React.ReactElement => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [selectedType, setSelectedType] = useState<CloudType>();
  const [selectedTag, setSelectedTag] = useState<number>();
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation("common");
  const { mutate } = useSWRConfig();
  const previewModal = useDisclosure();
  const folderModal = useDisclosure();
  const uploadModal = useDisclosure();
  const fileModal = useDisclosure();
  const chooseFolderToMoveModal = useDisclosure();
  const router = useRouter();
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const { data: tags } = useSWR<Tag[]>("/tag");
  const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
  const toast = useToastPromise();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const data = useCloud(
    folderId,
    debouncedSearchValue,
    [selectedType],
    selectedTag
  );

  const updateDir = useCallback(() => {
    mutate(
      folderId
        ? `/cloud/dir/${folderId}${searchValue ? `?search=${searchValue}` : ""}`
        : null
    ),
      data?.mutate();
  }, [folderId, searchValue, data]);

  const onPreviewFile = useCallback((file) => {
    setSelectedFile(file);
    previewModal.onOpen();
  }, []);

  const onEditFile = useCallback((file) => {
    setSelectedFile(file);
    fileModal.onOpen();
  }, []);

  const onSelectTag = useCallback((tag) => {
    setSelectedTag((selectedTag) => (tag !== selectedTag ? tag : null));
  }, []);

  const onSelectType = useCallback((type) => {
    setSelectedType((selectedType) => (type !== selectedType ? type : null));
  }, []);

  const onSearch = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const onFolderClick = useCallback((slug: string, path?: string) => {
    if (path) router.push(`/cloud/${path}`);
    else router.push(`${router.asPath}/${slug}`);
    setSearchValue("");
  }, []);

  const selectedFilesAndFoldersCount = useMemo(
    () => selectedFiles?.length + selectedFolders?.length,
    [selectedFiles, selectedFolders]
  );

  const onFolderCheck = (
    e: React.ChangeEvent<HTMLInputElement>,
    folder: Folder
  ) => {
    if (e.currentTarget.checked)
      setSelectedFolders((foldersIds) => [...foldersIds, folder?.id]);
    else
      setSelectedFolders(
        selectedFolders?.filter(
          (selectedFolder) => selectedFolder !== folder?.id
        )
      );
  };

  const onFileCheck = (e: React.ChangeEvent<HTMLInputElement>, file: File) => {
    if (e?.currentTarget?.checked)
      setSelectedFiles((filesIds) => [...filesIds, file?.id]);
    else
      setSelectedFiles(selectedFiles?.filter((fileId) => fileId !== file?.id));
  };

  const onSelectAll = () => {
    setSelectedFolders(data?.folders?.map((folder) => folder?.id));
    setSelectedFiles(data?.files?.map((file) => file?.id));
  };

  const onUnSelectAll = () => {
    setSelectedFolders([]);
    setSelectedFiles([]);
  };

  const onDeleteSelected = () => {
    setDeleteLoading(true);

    toast.promise(
      axios
        .post("/cloud/delete", {
          files: selectedFiles,
          folders: selectedFolders,
        })
        .then(() => {
          onUnSelectAll();
          data?.mutate();
        })
        .finally(() => setDeleteLoading(false)),
      {
        success: t("cloud.deleteSelectedSuccess"),
        pending: t("cloud.deleteSelectedPending"),
        error: t("cloud.deletedSelectedError"),
      }
    );
  };

  return (
    <>
      <Flex mb={6} flexWrap={{ base: "wrap", md: "nowrap" }}>
        <Heading
          as="h2"
          size="lg"
          w={{ base: "100%", md: "23%" }}
          mr={{ base: 0, md: 6 }}
        >
          <Flex align="center">
            {t("cloud.cloud")}
            <HelpTooltip label="cloud.heading" />
          </Flex>
        </Heading>
        <Input
          type="text"
          placeholder={`${t("search")}...`}
          w={{ base: "100%", md: "16rem" }}
          size="xl"
          px={5}
          onChange={onSearch}
        />
        <Flex
          ml="auto"
          w={{ base: "100%", md: "auto" }}
          mt={{ base: 4, md: 0 }}
          alignItems="center"
          flexDirection={{ base: "column", md: "row" }}
        >
          {selectedFilesAndFoldersCount ? (
            <Menu>
              <MenuButton>
                <Button
                  mx={2}
                  rightIcon={<Icon as={ChevronDown} />}
                  variant="ghost"
                >
                  {t("cloud.selectedItems", {
                    count: selectedFilesAndFoldersCount,
                  })}
                </Button>
              </MenuButton>
              <MenuList>
                <MenuItem onClick={onSelectAll}>
                  {t("cloud.selectAll")}
                </MenuItem>
                <MenuItem onClick={onUnSelectAll}>
                  {t("cloud.unSelectAll")}
                </MenuItem>
                <MenuItem onClick={chooseFolderToMoveModal.onOpen}>
                  {t("move")}
                </MenuItem>
                <MenuItem onClick={onDeleteSelected} isDisabled={deleteLoading}>
                  {t("delete")}
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}
          <Button mt={{ base: 2, md: 0 }} onClick={uploadModal.onOpen}>
            {t("cloud.uploadFiles")}
          </Button>
          <Button
            ml={{ base: 0, md: 2 }}
            mt={{ base: 2, md: 0 }}
            onClick={folderModal.onOpen}
          >
            {t("cloud.addFolder")}
          </Button>
        </Flex>
      </Flex>

      <Flex align="flex-start" direction={{ base: "column", md: "row" }}>
        <CloudFilters
          selectedType={selectedType}
          selectedTag={selectedTag}
          onSelectTag={onSelectTag}
          onSelectType={onSelectType}
          tags={tags}
        />
        <CloudGrid
          data={{
            files: data?.files,
            folders: data?.folders,
            error: data?.error,
            hasNextPage: data?.hasNextPage,
            isLoading: data?.isLoading,
            loadMore: data?.loadMore,
            refresh: data?.mutate,
          }}
          isSearching={debouncedSearchValue !== ""}
          onUpdate={updateDir}
          onEditFile={onEditFile}
          onPreviewFile={onPreviewFile}
          onFolderClick={onFolderClick}
          onSelectTag={onSelectTag}
          onFolderCheck={onFolderCheck}
          checkedFoldersIds={selectedFolders}
          onFileCheck={onFileCheck}
          checkedFilesIds={selectedFiles}
        />
      </Flex>

      {folderModal.isOpen && (
        <FolderModal
          parentFolder={folderId}
          isOpen={folderModal.isOpen}
          onClose={folderModal.onClose}
          onUpdate={updateDir}
        />
      )}

      {uploadModal.isOpen && (
        <UploadFilesModal
          parentFolder={folderId}
          isOpen={uploadModal.isOpen}
          onClose={uploadModal.onClose}
          onUpdate={updateDir}
        />
      )}

      {previewModal.isOpen && (
        <PreviewModal
          file={selectedFile}
          isOpen={previewModal.isOpen}
          onClose={previewModal.onClose}
        />
      )}

      {fileModal.isOpen && (
        <FileModal
          file={selectedFile}
          onUpdate={updateDir}
          isOpen={fileModal.isOpen}
          onClose={fileModal.onClose}
        />
      )}

      {chooseFolderToMoveModal.isOpen && (
        <ChooseFolderToMoveModal
          isOpen={chooseFolderToMoveModal.isOpen}
          onClose={chooseFolderToMoveModal.onClose}
          selectedFiles={selectedFiles}
          selectedFolders={selectedFolders}
          setSelectedFolders={setSelectedFolders}
          setSelectedFiles={setSelectedFiles}
          refreshData={data?.mutate}
        />
      )}
    </>
  );
};

export default Cloud;
